import { Component, inject, Input, OnChanges, OnInit, SimpleChanges, signal, effect } from '@angular/core';
import { ImageService } from '../../../../../services/image/image.service';
import { FormatService } from '../../../../../common/format/format.service';
import { ApplicationStatusEnum, BriefApplicationResponseDto } from '../../../../../common/general-types/briefs.dto';
import { VideoResponseDto, VideoStatusEnum } from '../../../../../common/general-types/videos.dto';
import { CreatorResponseDto } from '../../../../../common/general-types/creators.dto';
import { VideoFeedbackComponent } from '../../../../../pages/view-submission/molecules/video-feedback/video-feedback.component';
import { HasBoltOnDirectiveDirective } from '../../../../../common/directives/has-bolt-on/has-bolt-on-directive.directive';
import { environment } from '../../../../../../environments/environment';
import { NotHasBoltOnDirectiveDirective } from '../../../../../common/directives/not-has-bolt-on/not-has-bolt-on-directive.directive';
import { ChoosePlanService } from '../../../../choose-plan/service/choose-plan/choose-plan.service';
import { Router } from '@angular/router';
import { VideoThumbnailComponent } from '@/app/atoms/video-thumbnail/video-thumbnail.component';
import { ManageBriefTabsService } from '../../../service/manage-brief-tabs/manage-brief-tabs.service';

@Component({
  selector: 'brief-feed-started',
  standalone: true,
  imports: [
    VideoFeedbackComponent,
    HasBoltOnDirectiveDirective,
    NotHasBoltOnDirectiveDirective,
    VideoThumbnailComponent ],
  templateUrl: './feed-started.component.html',
  styleUrl: './feed-started.component.css'
})
export class FeedStartedComponent implements OnInit, OnChanges {

  environment = environment;
  protected readonly VideoStatusEnum = VideoStatusEnum;

  @Input('activity') activity!: {
    applicationStatus: ApplicationStatusEnum;
    date: Date;
    briefApplications: Array<BriefApplicationResponseDto & { applicationVideos: VideoResponseDto[] }>;
  };

  showVideoFeedback = signal<VideoResponseDto | undefined>(undefined);
  creator = signal<CreatorResponseDto | undefined>(undefined);
  selectedBriefApplication = signal<(BriefApplicationResponseDto & { applicationVideos?: VideoResponseDto[] }) | undefined>(undefined);

  defaultMessageText = signal<string | undefined>(undefined);

  /** Cached relative times — avoids NG0100 from `getTimeSinceUpdated` changing between CD passes. */
  headerTimeSince = signal<string>('...');
  private readonly videoTimeSinceById = new Map<string, string>();

  /** Services */
  private router = inject(Router);
  public formatService = inject(FormatService);
  public imageService = inject(ImageService);
  public choosePlanService = inject(ChoosePlanService);
  private manageBriefTabsService = inject(ManageBriefTabsService);
  /** Services END */

  constructor() {
    effect(() => {
      this.openFeedbackFromRoute(this.manageBriefTabsService.videoFeedbackCreatorId());
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['activity']) {
      this.refreshRelativeTimeLabels();
      this.openFeedbackFromRoute(this.manageBriefTabsService.videoFeedbackCreatorId());
    }
  }

  ngOnInit(): void {
    this.creator.set(this.activity.briefApplications[0]?.creator ?? undefined);
    this.refreshRelativeTimeLabels();
    this.openFeedbackFromRoute(this.manageBriefTabsService.videoFeedbackCreatorId());
  }

  private refreshRelativeTimeLabels(): void {
    this.videoTimeSinceById.clear();
    if (!this.activity?.date) {
      this.headerTimeSince.set('...');
      return;
    }
    this.headerTimeSince.set(this.formatService.getTimeSinceUpdated(this.activity.date));
    for (const application of this.activity.briefApplications ?? []) {
      for (const video of application.applicationVideos ?? []) {
        this.videoTimeSinceById.set(video.id, this.formatService.getTimeSinceUpdated(video.createdDate));
      }
    }
  }

  videoTimeSinceLabel(video: VideoResponseDto): string {
    return this.videoTimeSinceById.get(video.id) ?? '';
  }

  viewContent(video: VideoResponseDto, application: BriefApplicationResponseDto & { applicationVideos?: VideoResponseDto[] }) {
    this.showVideoFeedback.set(video);
    this.creator.set(video.creator);
    this.selectedBriefApplication.set(application);
    const creatorId = video.creator?.blazeFirebaseUid ?? application.creator?.blazeFirebaseUid;
    if (creatorId) {
      this.manageBriefTabsService.navigateToVideoFeedback(creatorId);
    }
  }

  closeVideoFeedback(): void {
    this.showVideoFeedback.set(undefined);
    this.manageBriefTabsService.clearVideoFeedbackRoute();
  }

  private openFeedbackFromRoute(creatorId: string | undefined): void {
    if (!creatorId) {
      return;
    }
    if (this.showVideoFeedback() && this.creator()?.blazeFirebaseUid === creatorId) {
      return;
    }
    for (const application of this.activity?.briefApplications ?? []) {
      const videos = application.applicationVideos ?? [];
      const video =
        videos.find((item) => item.creator?.blazeFirebaseUid === creatorId) ??
        (application.creator?.blazeFirebaseUid === creatorId ? videos[0] : undefined);
      if (!video) {
        continue;
      }
      this.showVideoFeedback.set(video);
      this.creator.set(video.creator ?? application.creator);
      this.selectedBriefApplication.set(application);
      return;
    }
  }

  getIsUgcOnly(): boolean {
    const app = this.selectedBriefApplication();
    return !(app?.brief?.shoutBrief);
  }

  sendSuggestedFeedback(video: VideoResponseDto, application: BriefApplicationResponseDto) {
    this.defaultMessageText.set(`Hello, Thank you for your submission 😊. \nHere are some suggested updates for your next draft:\n\nPlease review the suggested updates and make the necessary changes.\nThank you for your time and effort. `);
    this.viewContent(video, application);
  }

  openChoosePlan() {
    this.choosePlanService.launchSubscribeOrBoltOn(environment.stripe.shaas21products.bo_ai_moderation);
  }

  aiReviewSuggestion(videoAiModeration: any): 'pass' | 'review' | 'fail' {
    const moderation = videoAiModeration;
    if (!moderation?.reviewResults?.length) {
      return 'review';
    }

    const reviewResults = moderation.reviewResults;
    const score = moderation.score ?? 0;
    const SCORE_THRESHOLD = 75;

    const hasUnapprovedDont = reviewResults.some(
      (r: any) => r.type === 'dont' && r.answer !== 'Approved'
    );

    // 1. Any dont not approved and score < 75% → fail
    // 2. Any dont not approved and score >= 75% → review
    if (hasUnapprovedDont) {
      return score < SCORE_THRESHOLD ? 'fail' : 'review';
    }

    // 3. All donts approved: score < 75% → review, otherwise pass
    return score < SCORE_THRESHOLD ? 'review' : 'pass';
  }

  hasSuggestedFix(videoAiModeration: any) {
    let hasSuggestedFix = false;
    if (videoAiModeration && videoAiModeration.reviewResults) {
      for (let reviewResult of videoAiModeration.reviewResults) {
        if (reviewResult.suggestedFix) {
          hasSuggestedFix = true;
          break;
        }
      }
    }
    return hasSuggestedFix;
  }

  suggestedFixes(videoAiModeration: any) {
    const reviewResults = videoAiModeration?.reviewResults ?? [];
    return reviewResults
      .filter((r: any) => r.suggestedFix)
      .map((r: any) => r.suggestedFix as string);
  }

  goToApplication() {
    console.log('goToApplication()');
    if (this.activity && this.activity.briefApplications && this.activity.briefApplications.length > 0) {
      this.router.navigate([`/view-submission/${this.activity.briefApplications[0]?.briefId}/${this.activity.briefApplications[0]?.creator?.blazeFirebaseUid}`]);
    }
  }
}
